import { useCallback, useState } from "react";
import styled from "styled-components";

import { ReactComponent as CleaningIcon } from "../../../assets/img/auth_onboarding/work_types/cleaning.svg";
// Import work type icons
import { ReactComponent as ConstructionIcon } from "../../../assets/img/auth_onboarding/work_types/construction.svg";
import { ReactComponent as EducationIcon } from "../../../assets/img/auth_onboarding/work_types/education.svg";
import { ReactComponent as ElectricalIcon } from "../../../assets/img/auth_onboarding/work_types/electrical.svg";
import { ReactComponent as EventsIcon } from "../../../assets/img/auth_onboarding/work_types/events.svg";
import { ReactComponent as GasOilIcon } from "../../../assets/img/auth_onboarding/work_types/gas_oil.svg";
// Additional work type icons
import { ReactComponent as GeneralContractingIcon } from "../../../assets/img/auth_onboarding/work_types/general_contracting.svg";
import { ReactComponent as GovernmentalIcon } from "../../../assets/img/auth_onboarding/work_types/governmental.svg";
import { ReactComponent as HandymanIcon } from "../../../assets/img/auth_onboarding/work_types/handyman.svg";
import { ReactComponent as HealthcareIcon } from "../../../assets/img/auth_onboarding/work_types/healthcare.svg";
import { ReactComponent as HospitalityIcon } from "../../../assets/img/auth_onboarding/work_types/hospitality.svg";
import { ReactComponent as LawnCareIcon } from "../../../assets/img/auth_onboarding/work_types/lawn_care.svg";
import { ReactComponent as LogisticsIcon } from "../../../assets/img/auth_onboarding/work_types/logistics.svg";
import { ReactComponent as ManufacturingIcon } from "../../../assets/img/auth_onboarding/work_types/manufacturing.svg";
import { ReactComponent as PlumbingIcon } from "../../../assets/img/auth_onboarding/work_types/plumbing.svg";
import { ReactComponent as PropertyManagementIcon } from "../../../assets/img/auth_onboarding/work_types/property_management.svg";
import { ReactComponent as RetailIcon } from "../../../assets/img/auth_onboarding/work_types/retail.svg";
import { ReactComponent as RoofingIcon } from "../../../assets/img/auth_onboarding/work_types/roofing.svg";
import { ReactComponent as SecurityIcon } from "../../../assets/img/auth_onboarding/work_types/security.svg";
import { ReactComponent as TransportationIcon } from "../../../assets/img/auth_onboarding/work_types/transportation.svg";
import { Icon, icons } from "../../../components/Icon";
import { BackButton, PrimaryButton } from "../../AuthNew/shared/authStyles";
import { TickCircleFilledIcon } from "../../AuthNew/shared/icons/AuthIcons";
import { AnimatePresence, motion } from "../../AuthNew/shared/motionUtils";
import {
  ButtonGroup,
  ModalSubtitle,
  ModalTitle,
  slideInRight,
} from "../shared/onboardingStyles";

// Progress Bar (Rectangle 5) - matches Step1Organization and Step2OrganizationType
const ProgressBarContainer = styled.div`
  width: 100%;
  height: 10px;
  background: #eef1f7;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 0;
  position: relative;
`;

const ProgressBarFill = styled(motion.div)`
  height: 100%;
  background: #aa8ef6;
  border-radius: 4px;
  transition: width 0.5s ease-in-out;
  position: absolute;
  top: 0;
  left: 0;
`;

// Main Content Container - scrollable when showing additional options
const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  max-width: 400px;
  ${({ isScrollable }) =>
    isScrollable
      ? `
    max-height: calc(100vh - 400px);
    overflow-y: auto;
    overflow-x: hidden;
    padding: 0 5px 0 5px;
    
    /* Custom scrollbar styling */
    &::-webkit-scrollbar {
      width: 6px;
    }
    
    &::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 3px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: #cccfd4;
      border-radius: 3px;
      
      &:hover {
        background: #999faa;
      }
    }
  `
      : ""}
`;

// Section Container
const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

// Section Title
const SectionTitle = styled.p`
  font-family: "Mona Sans", sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 21px;
  color: #000e2a;
  margin: 0;
`;

// Options Grid Container
const OptionsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

// Row Container for options
const OptionsRow = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  flex-wrap: wrap;
`;

// Team Size Button
const TeamSizeButton = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 20px;
  height: 40px;
  border: 1px solid ${({ isSelected }) => (isSelected ? "#2266ef" : "#cccfd4")};
  border-radius: 12px;
  background: white;
  cursor: pointer;
  flex: 1 0 0;
  min-width: 0;
  transition: all 0.2s ease;
  box-shadow: ${({ isSelected }) =>
    isSelected ? "0px 4px 20px 0px rgba(34, 102, 239, 0.15)" : "none"};

  &:hover {
    border-color: ${({ isSelected }) => (isSelected ? "#2266ef" : "#999faa")};
  }
`;

const TeamSizeText = styled.p`
  font-family: "Mona Sans", sans-serif;
  font-size: 14px;
  font-weight: ${({ isSelected }) => (isSelected ? "700" : "400")};
  line-height: 21px;
  color: ${({ isSelected }) => (isSelected ? "#000e2a" : "#353a46")};
  margin: 0;
`;

// Work Type Button
const WorkTypeButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  height: 40px;
  border: 1px solid ${({ isSelected }) => (isSelected ? "#2266ef" : "#cccfd4")};
  border-radius: 12px;
  background: white;
  cursor: pointer;
  flex: 1 0 0;
  min-width: 0;
  transition: all 0.2s ease;
  box-shadow: ${({ isSelected }) =>
    isSelected ? "0px 4px 20px 0px rgba(34, 102, 239, 0.15)" : "none"};

  &:hover {
    border-color: ${({ isSelected }) => (isSelected ? "#2266ef" : "#999faa")};
  }
`;

const WorkTypeIcon = styled.div`
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ isSelected }) => (isSelected ? "#2266ef" : "#353a46")};

  svg {
    width: 100%;
    height: 100%;
  }
`;

const WorkTypeText = styled.p`
  font-family: "Mona Sans", sans-serif;
  font-size: 14px;
  font-weight: ${({ isSelected }) => (isSelected ? "700" : "400")};
  line-height: 21px;
  color: ${({ isSelected }) => (isSelected ? "#2266ef" : "#353a46")};
  margin: 0;
`;

const Step3TeamSetup = ({
  data,
  onChange,
  onNext,
  onBack,
  progress = 50, // Default progress for step 3 of 6
}) => {
  const [showOthers, setShowOthers] = useState(false);

  // Team size options
  const teamSizes = [
    { value: "1", label: "Just Me" },
    { value: "2-10", label: "2-10" },
    { value: "11-50", label: "11-50" },
    { value: "51-200", label: "51-200" },
    { value: "200-1000", label: "200-1000" },
    { value: "1000+", label: "1K+" },
  ];

  // Initial work types with icons
  const initialWorkTypes = [
    { value: "construction", label: "Construction", icon: ConstructionIcon },
    { value: "cleaning", label: "Cleaning", icon: CleaningIcon },
    { value: "plumbing", label: "Plumbing", icon: PlumbingIcon },
    {
      value: "transportation",
      label: "Transportation",
      icon: TransportationIcon,
    },
    { value: "electrical", label: "Electrical", icon: ElectricalIcon },
    { value: "manufacturing", label: "Manufacturing", icon: ManufacturingIcon },
  ];

  // Additional work types (shown when "Others" is clicked)
  const additionalWorkTypes = [
    {
      value: "general-contracting",
      label: "General Contracting",
      icon: GeneralContractingIcon,
    },
    { value: "logistics", label: "Logistics", icon: LogisticsIcon },
    { value: "lawn-care", label: "Lawn Care", icon: LawnCareIcon },
    { value: "handyman", label: "Handyman", icon: HandymanIcon },
    { value: "roofing", label: "Roofing", icon: RoofingIcon },
    { value: "retail", label: "Retail", icon: RetailIcon },
    {
      value: "property-management",
      label: "Property Management",
      icon: PropertyManagementIcon,
    },
    { value: "security", label: "Security", icon: SecurityIcon },
    { value: "healthcare", label: "Healthcare", icon: HealthcareIcon },
    { value: "gas-oil", label: "Gas and Oil", icon: GasOilIcon },
    { value: "hospitality", label: "Hospitality", icon: HospitalityIcon },
    { value: "events", label: "Events", icon: EventsIcon },
    { value: "governmental", label: "Governmental", icon: GovernmentalIcon },
    { value: "education", label: "Education", icon: EducationIcon },
  ];

  // Combine all work types when "Others" is clicked
  const workTypes = showOthers
    ? [...initialWorkTypes, ...additionalWorkTypes]
    : initialWorkTypes;

  const selectedTeamSize = data.teamSize || "";
  const selectedWorkTypes = data.workTypes || [];

  const handleTeamSizeSelect = useCallback(
    size => {
      onChange({ teamSize: size });
    },
    [onChange],
  );

  const handleWorkTypeSelect = useCallback(
    type => {
      const currentWorkType = selectedWorkTypes && selectedWorkTypes[0]; // Get current single selection

      // If clicking the same one, deselect it
      if (currentWorkType === type) {
        onChange({ workTypes: [] });
        return;
      }

      // Set single selection (replace any existing selection)
      onChange({ workTypes: [type] });
    },
    [selectedWorkTypes, onChange],
  );

  const handleOthersClick = useCallback(() => {
    setShowOthers(true);
  }, []);

  const handleNext = useCallback(() => {
    if (selectedTeamSize) {
      onNext();
    }
  }, [selectedTeamSize, onNext]);

  const isNextDisabled = !selectedTeamSize;

  return (
    <motion.div
      variants={slideInRight}
      initial="initial"
      animate="animate"
      exit="exit"
    >
      {/* Progress Bar */}
      <ProgressBarContainer>
        <ProgressBarFill
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5, ease: "easeInOut" }}
        />
      </ProgressBarContainer>

      {/* Title and Subtitle */}
      <div style={{ marginTop: "32px" }}>
        <ModalTitle>Almost there!</ModalTitle>
        <ModalSubtitle>
          Help us create the perfect time tracking experience for you
        </ModalSubtitle>
      </div>

      {/* Content - Scrollable when showing additional options */}
      <ContentWrapper isScrollable={showOthers} style={{ marginTop: "32px" }}>
        {/* Team Size Section */}
        <SectionContainer>
          <SectionTitle>Members of your team</SectionTitle>
          <OptionsGrid>
            <OptionsRow>
              {teamSizes.slice(0, 3).map(size => {
                const isSelected = selectedTeamSize === size.value;
                return (
                  <TeamSizeButton
                    key={size.value}
                    type="button"
                    isSelected={isSelected}
                    onClick={() => handleTeamSizeSelect(size.value)}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                  >
                    {isSelected && (
                      <TickCircleFilledIcon
                        size="20px"
                        color="#2266ef"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      />
                    )}
                    <TeamSizeText isSelected={isSelected}>
                      {size.label}
                    </TeamSizeText>
                  </TeamSizeButton>
                );
              })}
            </OptionsRow>
            <OptionsRow style={{ justifyContent: "flex-end" }}>
              {teamSizes.slice(3).map(size => {
                const isSelected = selectedTeamSize === size.value;
                return (
                  <TeamSizeButton
                    key={size.value}
                    type="button"
                    isSelected={isSelected}
                    onClick={() => handleTeamSizeSelect(size.value)}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                  >
                    {isSelected && (
                      <TickCircleFilledIcon
                        size="20px"
                        color="#2266ef"
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      />
                    )}
                    <TeamSizeText isSelected={isSelected}>
                      {size.label}
                    </TeamSizeText>
                  </TeamSizeButton>
                );
              })}
            </OptionsRow>
          </OptionsGrid>
        </SectionContainer>

        {/* Work Types Section */}
        <SectionContainer>
          <SectionTitle>What kind of work do you do?</SectionTitle>
          <OptionsGrid>
            {/* Render work types in rows of 2 */}
            {workTypes.map((workType, index) => {
              const isSelected = selectedWorkTypes.includes(workType.value);
              // Render in pairs
              if (index % 2 === 0) {
                const nextWorkType = workTypes[index + 1];
                const IconComponent = workType.icon;
                const NextIconComponent = nextWorkType?.icon;

                return (
                  <OptionsRow key={`row-${index}`}>
                    <WorkTypeButton
                      type="button"
                      isSelected={isSelected}
                      onClick={() => handleWorkTypeSelect(workType.value)}
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                    >
                      {IconComponent && (
                        <WorkTypeIcon isSelected={isSelected}>
                          <IconComponent />
                        </WorkTypeIcon>
                      )}
                      <WorkTypeText isSelected={isSelected}>
                        {workType.label}
                      </WorkTypeText>
                    </WorkTypeButton>
                    {nextWorkType && (
                      <WorkTypeButton
                        type="button"
                        isSelected={selectedWorkTypes.includes(
                          nextWorkType.value,
                        )}
                        onClick={() => handleWorkTypeSelect(nextWorkType.value)}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                      >
                        {NextIconComponent && (
                          <WorkTypeIcon
                            isSelected={selectedWorkTypes.includes(
                              nextWorkType.value,
                            )}
                          >
                            <NextIconComponent />
                          </WorkTypeIcon>
                        )}
                        <WorkTypeText
                          isSelected={selectedWorkTypes.includes(
                            nextWorkType.value,
                          )}
                        >
                          {nextWorkType.label}
                        </WorkTypeText>
                      </WorkTypeButton>
                    )}
                  </OptionsRow>
                );
              }
              return null;
            })}

            {/* Others button - full width (only show when not showing others) */}
            {!showOthers && (
              <OptionsRow>
                <WorkTypeButton
                  type="button"
                  isSelected={false}
                  onClick={handleOthersClick}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  style={{ flex: "1 0 0" }}
                >
                  <WorkTypeText isSelected={false}>Others</WorkTypeText>
                </WorkTypeButton>
              </OptionsRow>
            )}

            {/* Something Else button - full width (only show when showing others) */}
            {showOthers && (
              <OptionsRow>
                <WorkTypeButton
                  type="button"
                  isSelected={selectedWorkTypes.includes("something-else")}
                  onClick={() => handleWorkTypeSelect("something-else")}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  style={{ flex: "1 0 0" }}
                >
                  <WorkTypeText
                    isSelected={selectedWorkTypes.includes("something-else")}
                  >
                    Something Else
                  </WorkTypeText>
                </WorkTypeButton>
              </OptionsRow>
            )}
          </OptionsGrid>
        </SectionContainer>
      </ContentWrapper>

      {/* Back and Next Buttons */}
      <ButtonGroup marginTop="32px" style={{ maxWidth: "400px" }}>
        <BackButton
          type="button"
          onClick={onBack}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          height="40px"
          style={{
            background: "#e9f0fe",
            borderRadius: "32px",
            padding: "8px 16px",
            width: "100px",
            fontSize: "14px",
            fontWeight: 700,
            color: "#000e2a",
            border: "none",
          }}
        >
          <Icon component={icons.back} />
          Back
        </BackButton>
        <PrimaryButton
          type="button"
          onClick={handleNext}
          disabled={isNextDisabled}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          height="40px"
          fontSize="16px"
          style={{
            flex: 1,
            borderRadius: "32px",
            padding: "8px 16px",
            background: "linear-gradient(to right, #2266ef 0%, #6c2ef2 100%)",
            color: "white",
            fontWeight: 700,
          }}
        >
          Next
        </PrimaryButton>
      </ButtonGroup>
    </motion.div>
  );
};

export default Step3TeamSetup;
